import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  TextField,
  MenuItem,
  Typography,
} from "@mui/material";
import { CustomTitle } from "../../../components/Custom/CustomTypography";
import { useAlert } from "../../../components/Alert";
import api from "../../../globals/axios";
import { useNavigate } from "react-router-dom";
import {
  fpoInterestFormFields,
  commodityDetailFields,
} from "../../../Data/fieldData";
import { useTranslation } from "react-i18next";
import { fpoInterestValidationSchema } from "../../../components/Custom/ValidationSchema";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SimpleHeader from "../../../components/SimpleHeader";

const FPOInterestForm = () => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const [formData, setFormData] = useState({
    fpo_name: "",
    contact_person_name: "",
    contact: "",
    location: "",
    total_number_of_farmers: "",
    total_acreage: "",
    interested_in: "",
    commodity_details: [
      {
        commodity: "",
        quantity: "",
        availability: "",
        harvest_date: dayjs().add(1, "month"),
      },
    ],
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commodityOptions, setCommodityOptions] = useState([]);
  const navigate = useNavigate();

  // Fetch commodity options
  const fetchCommoditiesList = async () => {
    try {
      const response = await api.get("/common/commodities/");
      setCommodityOptions(response.data);
    } catch (error) {
      console.error("Error Fetching Commodities list:", error);
    }
  };

  useEffect(() => {
    fetchCommoditiesList();
  }, []);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    try {
      await fpoInterestValidationSchema.validateAt(name, { [name]: value });
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [name]: error.message }));
    }
  };

  const handleCommodityChange = (index, e) => {
    const { name, value } = e.target;
    const updatedCommodityDetails = [...formData.commodity_details];
    updatedCommodityDetails[index][name] = value;
    setFormData((prev) => ({
      ...prev,
      commodity_details: updatedCommodityDetails,
    }));
  };

  const handleDateChange = (index, date) => {
    const updatedCommodityDetails = [...formData.commodity_details];
    updatedCommodityDetails[index].harvest_date = dayjs(date);
    setFormData((prev) => ({
      ...prev,
      commodity_details: updatedCommodityDetails,
    }));
  };

  const addCommodityDetail = () => {
    setFormData((prev) => ({
      ...prev,
      commodity_details: [
        ...prev.commodity_details,
        {
          commodity: "",
          quantity: "",
          availability: "",
          harvest_date: dayjs().add(1, "month"),
        },
      ],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const cleanedFormData = {
        ...formData,
        commodity_details: formData.commodity_details.map((detail) => {
          if (detail.availability !== "future") {
            const { harvest_date, ...rest } = detail;
            console.log(
              "Skipping harvest_date for non-future commodity:",
              harvest_date
            );
            return rest;
          }
          return {
            ...detail,
            harvest_date: dayjs(detail.harvest_date).format("YYYY-MM-DD"),
          };
        }),
      };

      await fpoInterestValidationSchema.validate(cleanedFormData, {
        abortEarly: false,
      });
      const response = await api.post("/fpo/fpo-interests/", cleanedFormData);
      console.log("API Response:", response);
      showAlert("success", "Successfully submitted your details.");
      navigate("/success-page", {
        state: {
          username: response.data.user_credentials.username,
          password: response.data.user_credentials.password,
        },
      });
    } catch (error) {
      console.error("Validation or submission error:", error);
      if (error.inner) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
        console.error("Validation errors:", validationErrors);
      } else {
        const errorMessage =
          error.response?.data?.message ||
          "Some error occurred while submitting the details";
        showAlert("error", errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formFields = fpoInterestFormFields(t);
  const commodityFields = formData.commodity_details.map((detail) =>
    commodityDetailFields(t, commodityOptions, detail.availability)
  );

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <SimpleHeader />
        <Container maxWidth="md">
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
            <CustomTitle>{t("FPO Interest Form")}</CustomTitle>
            <Grid container spacing={3} mt={1}>
              {formFields.map((field) => (
                <Grid item xs={12} key={field.name}>
                  {field.type === "text" && (
                    <TextField
                      name={field.name}
                      label={field.label}
                      value={formData[field.name]}
                      onChange={handleChange}
                      fullWidth
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                    />
                  )}
                  {field.type === "number" && (
                    <TextField
                      name={field.name}
                      label={field.label}
                      type="number"
                      value={formData[field.name]}
                      onChange={handleChange}
                      fullWidth
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                    />
                  )}
                  {field.type === "select" && (
                    <FormControl fullWidth error={!!errors[field.name]}>
                      <InputLabel>{field.label}</InputLabel>
                      <Select
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        label={field.label}
                      >
                        {field.options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors[field.name] && (
                        <Typography color="error">
                          {errors[field.name]}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                </Grid>
              ))}
            </Grid>

            <Box container mt={2} borderRadius={2}>
              <Typography variant="h6" gutterBottom>
                {t("Commodities to sell")}
              </Typography>
              {formData.commodity_details.map((commodityDetail, index) => (
                <Box
                  key={index}
                  mt={2}
                  sx={{ border: "1px solid #A5A5A5", padding: "15px" }}
                >
                  {commodityFields[index].map((field) => (
                    <Grid item xs={12} sm={6} key={field.name} padding={1}>
                      {field.type === "text" && (
                        <TextField
                          required
                          name={field.name}
                          label={field.label}
                          value={commodityDetail[field.name]}
                          onChange={(e) => handleCommodityChange(index, e)}
                          fullWidth
                          error={
                            !!errors[
                              `commodity_details[${index}].${field.name}`
                            ]
                          }
                          helperText={
                            errors[`commodity_details[${index}].${field.name}`]
                          }
                        />
                      )}
                      {field.type === "number" && (
                        <TextField
                          required
                          name={field.name}
                          label={field.label}
                          type="number"
                          value={commodityDetail[field.name]}
                          onChange={(e) => handleCommodityChange(index, e)}
                          fullWidth
                          error={
                            !!errors[
                              `commodity_details[${index}].${field.name}`
                            ]
                          }
                          helperText={
                            errors[`commodity_details[${index}].${field.name}`]
                          }
                        />
                      )}
                      {field.type === "select" && (
                        <FormControl
                          required
                          fullWidth
                          error={
                            !!errors[
                              `commodity_details[${index}].${field.name}`
                            ]
                          }
                        >
                          <InputLabel>{field.label}</InputLabel>
                          <Select
                            name={field.name}
                            value={commodityDetail[field.name]}
                            onChange={(e) => handleCommodityChange(index, e)}
                            label={field.label}
                          >
                            {field.options.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors[
                            `commodity_details[${index}].${field.name}`
                          ] && (
                            <Typography color="error">
                              {
                                errors[
                                  `commodity_details[${index}].${field.name}`
                                ]
                              }
                            </Typography>
                          )}
                        </FormControl>
                      )}
                      {field.type === "date" && (
                        <DatePicker
                          views={["year", "month"]}
                          label={field.label}
                          minDate={dayjs()}
                          maxDate={dayjs().add(2, "year")}
                          value={dayjs(commodityDetail.harvest_date) || null}
                          onChange={(newValue) =>
                            handleDateChange(index, newValue)
                          }
                          slotProps={{
                            textField: {
                              required: true,
                              fullWidth: true,
                              error:
                                !!errors[
                                  `commodity_details[${index}].${field.name}`
                                ],
                              helperText:
                                errors[
                                  `commodity_details[${index}].${field.name}`
                                ],
                            },
                          }}
                        />
                      )}
                    </Grid>
                  ))}
                </Box>
              ))}
            </Box>

            <Grid container spacing={1} mt={2}>
              <Grid item xs={12}>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={addCommodityDetail}
                  sx={{ marginBottom: 2 }}
                >
                  {t("Add More")}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={isSubmitting}
                  sx={{ color: "white", padding: "10px 0" }}
                >
                  {isSubmitting
                    ? `${t("Submitting")}...`
                    : t("Submit Interest")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </LocalizationProvider>
    </>
  );
};

export default FPOInterestForm;
